<template>
  <v-slide-y-transition group>
    <div v-if="ready" column key="main" class="color--nice-black full-height--without-banner">
      <section class="pt-8">
        <h1 class="font--m-1 mb-2">
          우리동네<br />
          크라우드 펀딩
        </h1>
        <p class="font--m-5">
          번개입금과 중소벤처기업부 X 소상공인시장진흥공단이 함께 진행하는 크라우드 펀딩입니다.
        </p>
      </section>

      <section class="full background--accent white--text">
        <div class="px py-3">
          <p class="font--m-6">나의 한도</p>
          <GetOffer
            :loading.sync="componentGetOffer.loading"
            :offer.sync="componentGetOffer.offer"
            :options="componentGetOffer.options"
          >
            <v-fade-transition hide-on-leave group>
              <v-layout
                v-if="!componentGetOffer.offer || componentGetOffer.loading"
                column
                justify-center
                key="offer-loading"
              >
                <!-- 로딩중과 아닐때 section 높이 동일하게 맞춤 -->
                <h1 class="font-size--28" style="margin-bottom: 14px">한도를 산출중 입니다...</h1>
              </v-layout>
              <template v-else>
                <h1 class="font-size--28" key="offer-amount">{{ loanAmount | commaFormat }}원</h1>
              </template>
            </v-fade-transition>
          </GetOffer>
        </div>
        <v-progress-linear
          v-if="componentGetOffer.loading"
          color="white"
          :indeterminate="true"
        ></v-progress-linear>
      </section>

      <section>
        <Description />
      </section>

      <section class="pt-8 pb-10">
        <ApiButton
          color="accent"
          :classes="['font--m-3']"
          buttonText="신청하기"
          loadingText="신청중 입니다."
          :callFn="request"
          :disabled="requestLoading || !requestable"
        />
      </section>

      <div class="px-5" v-if="['local', 'development'].includes(mode) && requests?.length > 0">
        <span style="color: red">
          ----------------------------------------------------------------------<br />[local,
          development] 에서만 보입니다.
        </span>
        <Requests :items="requests" />
      </div>
    </div>
  </v-slide-y-transition>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  data() {
    return {
      ready: false,
      requests: [],
      requestLoading: false,
      componentGetOffer: {
        loading: false,
        offer: null,
        options: {
          loanType: 'woori_crowd',
          repaymentPeriodInDays: 270,
        },
      },
    }
  },
  components: {
    ApiButton: () => import('@/components/loans-request/ApiButton.vue'),
    GetOffer: () => import('@/components/loan/GetOffer.vue'),
    Description: () => import('@/pages/landing/woori-crowd/limit-inquiry/Description.vue'),
    Requests: () => import('@/components/loans-request/Requests.vue'),
  },
  mounted() {
    this.ready = true
  },
  async created() {
    _meta({
      titles: ['메인', '우리동네크라우드'],
    })
    await this.getRequests()
  },
  computed: {
    ...mapState('auth', ['me']),
    ...mapGetters('evaluation', ['isExpire']),
    ...mapGetters('loan', ['ongoingWooricrowds']),
    executedWooriCrowd() {
      return this.ongoingWooricrowds.find((l) => l.status === 'executed')
    },
    loanAmount() {
      return (!this.componentGetOffer.loading && this.componentGetOffer.offer?.amount) || 0
    },
    requestable() {
      return this.form && !this.componentGetOffer.loading && this.loanAmount > 0
    },
    ...mapState('env', ['mode']),
    form() {
      if (!this.componentGetOffer.offer) {
        return null
      }

      const {
        commissionRate,
        repaymentRatio,
        interestRate,
        overdueInterestRate,
        amount,
        uuid: initOfferUuid,
        periodInDays: repaymentPeriodInDays,
        loanType: type,
      } = this.componentGetOffer.offer
      return {
        amount, // 요청금액 disbursements의 합과 같아야 한다. // 보통의 경우 자신의 출금계좌로 넣어서 요청
        type, // creditpay:외상, woori_crowd: 우리동네크라우드펀딩
        interestRate,
        overdueInterestRate,
        repaymentRatio,
        commissionRate,
        repaymentPeriodInDays,
        initOfferUuid,
        disbursements: [
          {
            ...this.me?.withdrawalAccount,
            remark: `${this.me?.name}_대출금지급`, // 입금명
            amount,
          },
        ], //필수 최소1개
      }
    },
    getRouterRequest() {
      // return this.requests.find((r) => ['init', 'approve'].includes(r.status))
      return this.requests?.find((r) => true)
    },
  },
  watch: {
    getRouterRequest: {
      deep: true,
      async handler(v) {
        if (!v) {
          return
        }
        if (['false', false].includes(this.$route.query.autoRedirect)) {
          return
        }
        await this.movePage()
      },
    },
  },
  methods: {
    ...mapActions('loansRequest', {
      storeAddRequest: 'addRequest',
      storeGetRequests: 'getRequests',
    }),
    ...mapActions('evaluation', ['addCreditEvaluations']),
    async getRequests() {
      const { data } = await this.storeGetRequests({ page: 1, size: 1000, type: 'woori_crowd' })
      this.requests = data
    },
    async request() {
      try {
        this.requestLoading = true
        if (this.isExpire) {
          const { isConfirmed } = await this.$swal.basic.confirm({
            html: '신용 인증 유효기간이 만료되어,<br> 신용 인증 페이지로 이동합니다',
          })

          if (!isConfirmed) {
            return
          }
          await this.addCreditEvaluations({
            redirectPath: `${this.$route.path}?requestProcess=true`,
          })
          return
        }

        if (this.me.isLoanable !== true) {
          await this.$swal.basic.alert({
            html: '대출신청을 할 수 없습니다. 고객센터로 문의 주세요.',
          })
          return
        }

        const v = await this.storeAddRequest(this.form)
        this.requests = [v, ...this.requests]
        await this.movePage()
      } finally {
        this.requestLoading = false
      }
    },
    async movePage() {
      const request = this.requests[0]
      const created = this.$moment(request.created).format('YYYY-MM-DD')
      const isOld = created < '2024-04-01'
      let isLoanOnGoing = false

      if (request.status === 'approve') {
        const { data } = await this.$defaultApi.getLoansRequestLoan(request.uuid)
        if (data?.uuid) {
          this.loan = data
          isLoanOnGoing = this.loan.status != 'finished' && this.loan.status != 'canceled'
        }
      }

      if (isLoanOnGoing || !isOld) {
        return await this.$router.replace(`/woori-crowd/${request.uuid}`)
      } else {
        return // do not move
      }
    },
  },
}
</script>

<style lang="scss" scoped>
p {
  margin-bottom: 0;
}
section:not(:last-of-type) {
  margin-bottom: 30px;
}

section:not(.full),
.px {
  padding: 0 12px;
}
</style>
